import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { all_routes } from "../router/all_routes";
import Table from "../../core/common/dataTable/index";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../core/data/redux/store";
import {
  deleteGoaltype,
  UpdateGoaltype,
} from "../../core/data/redux/performance/goaltype/goaltypeAction";
import DeleteModal1 from "../../core/modals/deleteModal1";
import Select from "react-select";
import { fetchAllIndicatorGroup } from "../../core/data/redux/performance/indicatorgroup/indicatorgroupAction";
import {
  createIndicator,
  fetchAllIndicator,
  updateIndicator,
  deleteIndicator,
} from "../../core/data/redux/performance/indicator/indicatorAction";
import { filterData } from "../../core/common/GlobalFunctions/tableFilters";
import { Drawer } from "antd";
import { message } from "antd";
import { decryptData } from "../../core/utils/encryptUtils";

const Indicator = () => {
  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];
  const [selectedStatus, setSelectedStatus] = useState("active");
  const dispatch = useDispatch<AppDispatch>();
  const [selectedSort, setSelectedSort] = useState<any>("Recently Added");
  const [tableKey, setTableKey] = useState(0);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const { user } = useSelector((state: any) => state.auth);
  const [Editing, setIsEditing] = useState<any>(false);
  const { indicatorgroup } = useSelector(
    (state: any) => state.performance.indicatorgroup
  );
  const { indicator, loadingStates } = useSelector(
    (state: any) => state.performance.indicator
  );
  const isLoading = Editing ? loadingStates.update : loadingStates.create;
  const [isDeleteModalVisible, setIsDeleteModalVisible] =
    useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<any>();
  const IndicatorId = selectedRowData?.Id;
  const [formData, setFormData] = useState<any>({
    indicator_name: "",
    description: "",
    status: statusOptions[0],
    indicator_group_id: null,
  });

  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");

  const formattedIndicatorGroup = indicatorgroup
    ?.filter((group: any) => group.Status.toLowerCase() === "active")
    .map((group: any) => ({
      label: group.Name,
      value: group.Id,
    }));

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.indicator_name.trim()) {
      newErrors.indicator_name = "Name is required";
    }

    if (!formData.indicator_group_id) {
      newErrors.indicator_group_id = "Parent group is required";
    }

    if (!formData.status || !formData.status.value) {
      newErrors.status = "Status is required";
    }

    return newErrors;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev: any) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSelectChange = (selectedOption: any, field: string) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: selectedOption,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      const firstErrorKey = Object.keys(errors)[0];
      message.error(errors[firstErrorKey]);
      return;
    }

    const payload = {
      ...formData,
      ...(Editing
        ? { indicator_group_id: formData.indicator_group_id }
        : { indicator_group_id: formData.indicator_group_id.value }),
      status: formData.status?.value || null,
      ...(Editing ? { modifiedBy: user.UserId } : { createdBy: user.UserId }),
    };

    if (Editing) {
      dispatch(updateIndicator({ IndicatorId, data: payload })).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          message.success(res.payload.message);
          setTimeout(() => {
            dispatch(fetchAllIndicator());
          }, 1000);
        } else {
          alert(res.payload.error);
        }
      });
    } else {
      dispatch(createIndicator(payload)).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          message.warning(res.payload.message);
          setFormData({
            indicator_name: "",
            description: "",
            status: statusOptions[0],
            indicator_group_id: null,
          });
          setIsDrawerVisible(false);
          dispatch(fetchAllIndicator());
        } else {
          message.warning(res.payload.message);
        }
      });
    }
  };

  const handleEdit = (record: any): void => {
    setSelectedRowData(record);
    setFormData({
      indicator_name: record.Name,
      description: record.Description,
      status: statusOptions.find((opt: any) => opt.value === record.Status),
      indicator_group_id: record.indicator_group_id,
    });
  };

  useEffect(() => {
    dispatch(fetchAllIndicatorGroup());
    dispatch(fetchAllIndicator());
  }, []);

  const handleDeletePolicy = (IndicatorId: any) => {
    dispatch(deleteIndicator(IndicatorId))
      .then((resp: any) => {
        if (resp.meta.requestStatus === "fulfilled") {
          setIsDeleteModalVisible(false);
          alert(resp.payload.message);
          dispatch(fetchAllIndicator());
        } else {
          setIsDeleteModalVisible(false);
          alert(resp.payload.message);
        }
      })
      .catch((error: any) => {
        console.error("error is ", error);
      });
  };

  const data = indicator ? indicator : [];
  const filteredData = useMemo(() => {
    return filterData(
      data || [],
      { Status: selectedStatus },
      "CreatedAt",
      "CreatedAt",
      selectedSort
    );
  }, [indicator, selectedStatus, selectedSort]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [dispatch, indicator, selectedStatus, selectedSort]);

  const routes = all_routes;

  const columns = [
    {
      title: "Name",
      dataIndex: "Name",
      sorter: (a: any, b: any) => a.Name.length - b.Name.length,
    },
    {
      title: "Description",
      dataIndex: "Description",
      sorter: (a: any, b: any) => a.Description.length - b.Description.length,
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (text: string) => {
        const badgeClass =
          text.toLowerCase() === "active"
            ? "badge-success"
            : text.toLowerCase() === "inactive"
              ? "badge-danger"
              : "badge-secondary"; // fallback color

        return (
          <span
            className={`badge ${badgeClass} d-inline-flex align-items-center badge-xs`}
          >
            <i className="ti ti-point-filled me-1" />
            {text ? text.charAt(0).toUpperCase() + text.slice(1) : ""}

          </span>
        );
      },
      sorter: (a: any, b: any) => a.Status.length - b.Status.length,
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div className="action-icon d-inline-flex gap-2">
          {/* Edit Button - Always Active */}
          <button
            onClick={() => { if (!canEdit) return; setIsEditing(true); handleEdit(record); setIsDrawerVisible(true); }}
            style={{ cursor: canEdit ? "pointer" : "not-allowed", background: "none", border: "none", padding: 0, opacity: canEdit ? 1 : 0.5, }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={!canEdit ? "Permission Denied" : "Edit"}
            disabled={!canEdit}
          >
            <i className="ti ti-edit" />
          </button>

          <button
            onClick={() => {if (!canDelete || record.Status === "inactive") return;setSelectedRowData(record);setIsDeleteModalVisible(true);}}
            disabled={!canDelete || record.Status === "inactive"}
            style={{cursor: !canDelete || record.Status === "inactive" ? "not-allowed" : "pointer",opacity: !canDelete || record.Status === "inactive" ? 0.5 : 1,background: "none",border: "none",padding: 0,}}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={!canDelete ? "Permission Denied" : record.Status === "inactive" ? "Item is inactive" : "Delete"}
          >
            <i className="ti ti-trash" />
          </button>
        </div>
      ),
    },
  ];
  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Indicator</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Performance</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Add New Indicator{" "}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              {canAdd && (
                <div className="mb-2">
                  <Link
                    to="#"
                    className="btn btn-primary d-flex align-items-center"
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({
                        indicator_name: "",
                        description: "",
                        status: statusOptions[0],
                        indicator_group_id: null,
                      });
                      setIsDrawerVisible(true);
                    }}
                  >
                    <i className="ti ti-circle-plus me-2" />
                    Add Indicator{" "}
                  </Link>
                </div>
              )}
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          {/* Performance Indicator list */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Indicator List</h5>

              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-3">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedStatus
                      ? selectedStatus.charAt(0).toUpperCase() +
                      selectedStatus.slice(1)
                      : "Select Status"}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("active")}
                        className="dropdown-item rounded-1"
                      >
                        Active
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("inactive")}
                        className="dropdown-item rounded-1"
                      >
                        Inactive
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    Sort By : Last 7 Days
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Recently Added
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Desending
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <Table
                key={tableKey}
                dataSource={filteredData}
                columns={columns}
                Selection={true}
              />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© TalentFlow.</p>
          <p>
            Designed &amp; Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>

      <Drawer
        title={`${Editing ? "Edit" : "Add"} Indicator`}
        placement="right"
        onClose={() => setIsDrawerVisible(false)}
        open={isDrawerVisible}
        width={480}
        style={{ marginTop: 50 }}
      >
        <form onSubmit={handleSubmit}>
          <div className="modal-body pb-0">
            <div className="row">
              {/* Name */}
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Name <span className="text-danger"> *</span>
                  </label>
                  <input
                    type="text"
                    name="indicator_name"
                    className="form-control"
                    value={formData.indicator_name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Parent Group */}
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Parent Group <span className="text-danger"> *</span>
                  </label>
                  <Select
                    className="select"
                    name="indicator_group_id"
                    options={formattedIndicatorGroup.sort((a: any, b: any) => a.label.localeCompare(b.label))}
                    value={formattedIndicatorGroup.find(
                      (group: any) =>
                        group.value === formData.indicator_group_id
                    )}
                    onChange={(selectedOption: any) =>
                      handleSelectChange(selectedOption, "indicator_group_id")
                    }
                  />
                </div>
              </div>
              {/* Status */}
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Status <span className="text-danger"> *</span>
                  </label>
                  <Select
                    className="select"
                    options={statusOptions}
                    value={formData.status}
                    onChange={(selectedOption: any) =>
                      handleSelectChange(selectedOption, "status")
                    }
                  />
                </div>
              </div>
              {/* Description */}
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Description
                  </label>
                  <textarea
                    name="description"
                    className="form-control"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={() => {
                setIsDrawerVisible(false);
                setIsEditing(false);
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
              {Editing ? "Update" : "Add"} Indicator
            </button>
          </div>
        </form>
      </Drawer>

      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() =>
          selectedRowData?.Id && handleDeletePolicy(selectedRowData.Id)
        }
      />

      <style>
        {`
    .modal-right .modal-dialog {
      position: fixed;
      right: 0;
      margin: 0;
      width: 400px;
      height: 100%;
      transform: translateX(100%);
      transition: transform 0.3s ease-out;
      top: 0;
    }

    .modal.fade.modal-right.show .modal-dialog {
      transform: translateX(0);
    }

    .modal-content {
  height: 100%;
  border: none;
  border-radius: 0;
}

.show-right .modal-dialog {
  transform: translateX(100%);
  transition: transform 0.3s ease-out;
}

  `}
      </style>
    </>
  );
};

export default Indicator;
